import { Box, BoxProps, Image, ImageProps } from "@chakra-ui/react";
import { useCallback, useMemo } from "react";
import { Hrib } from "../../client";
import { defaultStreamUrl } from "../../utils/getStreamUrl";
import { thumbHashDataUrl } from "../../utils/thumbHash";

export interface IPivoImageProps extends BoxProps {
    id: Hrib;
    imageProps: ImageProps | React.ImgHTMLAttributes<HTMLImageElement>;
    sizes: string;
    thumbHash?: string | null;
}

export const PivoImage = ({ id, sizes, imageProps, thumbHash, ...pictureProps }: IPivoImageProps) => {
    const placeholder = useMemo(() => thumbHashDataUrl(thumbHash), [thumbHash]);

    const revealWhenLoaded = useCallback((img: HTMLImageElement | null) => {
        if (img === null) {
            return;
        }
        if (img.complete && img.naturalWidth > 0) {
            img.style.opacity = "1";
            return;
        }
        img.addEventListener("load", () => (img.style.opacity = "1"), { once: true });
    }, []);

    return (
        <Box
            as="picture"
            gridTemplateColumns="1fr"
            gridTemplateRows="1fr"
            {...pictureProps}
            style={{
                display: "grid",
                ...pictureProps.style,
            }}
        >
            {placeholder && (
                <Image src={placeholder} gridRowStart={1} gridColumnStart={1} {...imageProps} />
            )}

            <source
                srcSet={
                    defaultStreamUrl(id, "webp", "thumbnail") +
                    " 128w," +
                    defaultStreamUrl(id, "webp", "small") +
                    " 512w," +
                    defaultStreamUrl(id, "webp", "medium") +
                    " 768w," +
                    defaultStreamUrl(id, "webp", "large") +
                    " 1024w," +
                    defaultStreamUrl(id, "webp", "huge") +
                    " 2048w," +
                    defaultStreamUrl(id, "webp", "giant") +
                    " 4096w"
                }
                sizes={sizes}
                type="image/webp"
            />
            <source
                srcSet={
                    defaultStreamUrl(id, "jpeg", "thumbnail") +
                    " 128w," +
                    defaultStreamUrl(id, "jpeg", "small") +
                    " 512w," +
                    defaultStreamUrl(id, "jpeg", "medium") +
                    " 768w," +
                    defaultStreamUrl(id, "jpeg", "large") +
                    " 1024w," +
                    defaultStreamUrl(id, "jpeg", "huge") +
                    " 2048w," +
                    defaultStreamUrl(id, "jpeg", "giant") +
                    " 4096w"
                }
                sizes={sizes}
                type="image/jpeg"
            />
            <Image
                src={defaultStreamUrl(id)}
                gridRowStart={1}
                gridColumnStart={1}
                opacity="0"
                ref={revealWhenLoaded}
                transition="opacity 0.1s linear"
                {...imageProps}
            />
        </Box>
    );
};
